import { apiClient } from 'services/api-client';
import { TokenService } from './token.service';
import { User } from 'dtos/org/org.dto';
import { getCurrentAccessToken, isSupabaseAuthConfigured, supabase } from './supabase.client';

export interface RegisterResult {
  user: User | null;
  requiresEmailConfirmation: boolean;
}

export class AuthService {
  static async acceptInvitation(token: string): Promise<{ organization_id: string; role: string }> {
    const response = await apiClient.post<{ organization_id: string; role: string }>(`/invitations/${token}/accept`);
    return response.data;
  }
  static async register(name: string, email: string, password: string): Promise<RegisterResult> {
    if (!isSupabaseAuthConfigured || !supabase) {
      throw new Error('Supabase Auth is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
    }
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: name } },
    });
    if (error) throw error;
    if (!data.user) throw new Error('Account could not be created');
    if (!data.session) return { user: null, requiresEmailConfirmation: true };
    TokenService.saveAccessToken(data.session.access_token);
    const accessToken = await getCurrentAccessToken();
    if (!accessToken) throw new Error('Your account was created, but an authenticated session is not available yet. Confirm your email, then sign in to finish setup.');
    return {
      requiresEmailConfirmation: false,
      user: {
        id: data.user.id,
        email: data.user.email || email,
        full_name: name,
        role: data.user.app_metadata?.role || 'owner',
      },
    };
  }

  static async login(email: string, password: string): Promise<User> {
    if (isSupabaseAuthConfigured && supabase) {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error || !data.user || !data.session) throw error || new Error('Unable to sign in');
      TokenService.saveAccessToken(data.session.access_token);
      return {
        id: data.user.id,
        email: data.user.email || email,
        full_name: data.user.user_metadata?.full_name || email,
        role: data.user.app_metadata?.role || 'owner',
      };
    }
    throw new Error('Supabase Auth is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
  }

  static async requestPasswordReset(email: string): Promise<void> {
    if (!isSupabaseAuthConfigured || !supabase) {
      throw new Error('Supabase Auth is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  }

  static async hasRecoverySession(): Promise<boolean> {
    if (!isSupabaseAuthConfigured || !supabase) return false;
    const { data } = await supabase.auth.getSession();
    return Boolean(data.session);
  }

  static async updatePassword(password: string): Promise<void> {
    if (!isSupabaseAuthConfigured || !supabase) {
      throw new Error('Supabase Auth is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
    }
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  }

  static logout(): void {
    if (isSupabaseAuthConfigured && supabase) void supabase.auth.signOut();
    TokenService.clear();
  }
}
